<template>
  <section class="h-full mx-4" id="competitorMaterials">
    <BaseTable
      v-if="competitorMaterials"
      :headers="competitorMaterialsHeaders"
      :list="competitorMaterials"
      :total-records="competitorMaterialsTotalCount"
      :items-per-page="competitorMaterialsItemsPerPage"
      :selected-page="competitorMaterialsPageNumber"
      table-id="competitor-materials"
      clickable
      @refresh-data="onRefreshData"
      @delete-multiselect="onDeleteMultiselect"
      @add-new="onAddNewCompetitorMaterial"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onRowClicked">
      <template #actions="{ item }">
        <button
          v-if="item.isFromManager"
          type="button"
          class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
          @click="deleteSingleConfirm(item as CompetitorMaterial)">
          <IconTrashOutline class="size-4" />
        </button>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{
          $t('messages.info.deleteMaterial', {
            'material': competitorMaterialToDeleteSingle?.materialAlterCode.alterCode.name,
          })
        }}</template
      >
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import {
  getCompetitorMaterialsWithConditions,
  removeCompetitorMaterialsByIds,
} from '@/services/api/materials';
import BaseTable from '@/components/common/BaseTable.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type { CompetitorMaterial } from '@/types/Materials';
import type { TableFilterData } from '@/types/Base';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStateStore } from '@/stores/state';

const { t: $t } = useI18n({ useScope: 'global' });
const competitorMaterials = ref<CompetitorMaterial[]>();
const competitorMaterialsTotalCount = ref<number>();
const competitorMaterialsItemsPerPage = ref(50);
const competitorMaterialsPageNumber = ref(1);

const competitorMaterialsHeaders = [
  { value: 'material.name', text: $t('material.material'), type: 'text', active: true },
  { value: 'competitor.code', text: $t('material.competitor'), type: 'text', active: true },
  {
    value: 'materialAlterCode.alterCode.name',
    text: $t('material.alterCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.measurementUnit.name',
    text: $t('material.measurementUnit'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isDefault', text: $t('material.default'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('competitor-materials');
  if (storedTableData) {
    competitorMaterialsItemsPerPage.value = storedTableData.itemsPerPage;
    competitorMaterialsPageNumber.value = storedTableData.page;
  }
  const res = await getCompetitorMaterialsWithConditions({
    itemsPerPage: competitorMaterialsItemsPerPage.value,
    page: competitorMaterialsPageNumber.value,
  });

  if (res) {
    competitorMaterials.value = res.data;
    competitorMaterialsTotalCount.value = res.count;
  }
});

const filterIsActive = ref<boolean | undefined>(undefined);

async function onRefreshData(data: TableFilterData) {
  const sortColumn =
    data.column !== undefined ? competitorMaterialsHeaders[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  const res = await getCompetitorMaterialsWithConditions(params);
  if (res) {
    competitorMaterials.value = res.data;
    competitorMaterialsTotalCount.value = res.count;
  }
}

const toastStore = useToastStore();
const competitorMaterialToDeleteSingle = ref<CompetitorMaterial>();
const confirmDeleteSingleModalActive = ref(false);

async function onDeleteSingle() {
  if (!competitorMaterialToDeleteSingle.value) return;
  const idToDelete = competitorMaterialToDeleteSingle.value.id;

  const res = await removeCompetitorMaterialsByIds([idToDelete]);
  if (!res && competitorMaterials.value)
    competitorMaterials.value = competitorMaterials.value.filter((item) => item.id !== idToDelete);

  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  confirmDeleteSingleModalActive.value = false;
}

async function onDeleteMultiselect(items: CompetitorMaterial[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeCompetitorMaterialsByIds(idsToDelete);
  if (!res && competitorMaterials.value)
    competitorMaterials.value = competitorMaterials.value.filter(
      (item) => !idsToDelete.includes(item.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

function deleteSingleConfirm(item: CompetitorMaterial) {
  competitorMaterialToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  onRefreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  onRefreshData({});
}
const router = useRouter();

function onAddNewCompetitorMaterial() {
  router.push('/competitor-materials/wizard');
}

function onRowClicked(item: CompetitorMaterial) {
  router.push({
    path: '/competitor-materials/wizard',
    query: { id: item.materialId },
  });
}
</script>
